import React, { useState } from 'react';
import { getData, saveData, getUserProfile } from '../firebase';
import { exportToCSV } from '../exportUtils';

const BRAND = '#0f6e56';

// DSR columns we read (core money + key travel fields). Header match is case-insensitive & trims spaces.
// Each DSR row can create BOTH a sale (to client) and a purchase (from airline/supplier).
const DSR_COLUMNS = [
  'Invoice No.', 'Date', 'Airline Initial', 'PNR NO', 'Ticket-Number', 'PASSANGER NAME', 'ROUTING',
  'ISSUED FROM', 'BASIC', 'TAXES', 'TOTAL', 'Commission %', 'Commission amount', 'Supplier Service fee',
  'Net Supplier Total', 'Discount', 'Client Total', 'Client name', 'Travel date', 'Return Date',
  'Currency', 'FX Rate',
];

function norm(h) { return String(h || '').trim().toLowerCase().replace(/\s+/g, ' '); }

function parseCSV(text) {
  const rows = []; let field = '', row = [], inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQ) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') inQ = false;
      else field += c;
    } else {
      if (c === '"') inQ = true;
      else if (c === ',') { row.push(field); field = ''; }
      else if (c === '\n' || c === '\r') { if (c === '\r' && text[i + 1] === '\n') i++; row.push(field); rows.push(row); row = []; field = ''; }
      else field += c;
    }
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row); }
  return rows.filter(r => r.some(x => String(x).trim() !== ''));
}

export default function BulkUpload({ user }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('');
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState(0);
  const [baseCurrency, setBaseCurrency] = useState('USD');

  React.useEffect(() => { getUserProfile(user.uid).then(p => { if (p && p.currency) setBaseCurrency(p.currency); }); }, []);

  const template = () => {
    // A sample row mirroring the DSR format (core columns)
    const sample = [
      ['KIN/24/INV/000964', '2024-12-28', 'B6', '3NBBKP', '279-3584442489', 'SHEOSANKER KATHY', 'KIN-JFK-KIN',
        'STTS BRU', '362', '0', '362', '0', '0', '0', '362', '0', '550', 'Total Personal', '2025-01-24', '2025-01-27', '', ''],
    ];
    exportToCSV('finlybooks_DSR_template.csv', DSR_COLUMNS, sample);
  };

  const onFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const parsed = parseCSV(ev.target.result);
      if (parsed.length < 2) { setStatus('File looks empty or has no data rows.'); return; }
      const hdr = parsed[0];
      const idx = {};
      DSR_COLUMNS.forEach(col => {
        const pos = hdr.findIndex(h => norm(h) === norm(col));
        if (pos >= 0) idx[col] = pos;
      });
      // Need at least a client total OR net supplier total to do anything
      if (idx['Client Total'] == null && idx['Net Supplier Total'] == null) {
        setStatus('Could not find "Client Total" or "Net Supplier Total" columns. Please use the DSR template below.');
        return;
      }
      const get = (r, col) => idx[col] != null ? String(r[idx[col]] || '').trim() : '';
      const dataRows = parsed.slice(1).map(r => ({
        invNo: get(r, 'Invoice No.'), date: get(r, 'Date'), airline: get(r, 'Airline Initial'),
        pnr: get(r, 'PNR NO'), ticket: get(r, 'Ticket-Number'), pax: get(r, 'PASSANGER NAME'),
        routing: get(r, 'ROUTING'), issuedFrom: get(r, 'ISSUED FROM'), basic: get(r, 'BASIC'),
        taxes: get(r, 'TAXES'), total: get(r, 'TOTAL'), commPct: get(r, 'Commission %'),
        commAmt: get(r, 'Commission amount'), suppFee: get(r, 'Supplier Service fee'),
        netSupplier: get(r, 'Net Supplier Total'), discount: get(r, 'Discount'),
        clientTotal: get(r, 'Client Total'), clientName: get(r, 'Client name'),
        travelDate: get(r, 'Travel date'), returnDate: get(r, 'Return Date'),
        currency: get(r, 'Currency'), fxRate: get(r, 'FX Rate'),
      }));
      setRows(dataRows);
      setStatus(`${dataRows.length} DSR rows ready. Each row can create a client sale and a supplier purchase.`);
    };
    reader.readAsText(file);
  };

  const toISO = (d) => {
    if (!d) return new Date().toISOString().slice(0, 10);
    const dt = new Date(d);
    return isNaN(dt) ? new Date().toISOString().slice(0, 10) : dt.toISOString().slice(0, 10);
  };

  const rowStats = (r) => {
    const clientTotal = parseFloat(r.clientTotal) || 0;
    const netSupplier = parseFloat(r.netSupplier) || 0;
    const makesSale = clientTotal > 0 && !!(r.clientName || r.pax);
    const makesPurchase = netSupplier > 0 && !!(r.airline || r.issuedFrom);
    return { clientTotal, netSupplier, makesSale, makesPurchase, margin: clientTotal - netSupplier };
  };

  const doImport = async () => {
    setImporting(true); setDone(0);
    const existing = await getData(user.uid, 'invoices');
    let salesCount = existing.filter(i => i.type === 'sales').length;
    let purCount = existing.filter(i => i.type === 'purchase').length;
    let sales = 0, purch = 0, skip = 0;

    for (const r of rows) {
      const st = rowStats(r);
      if (!st.makesSale && !st.makesPurchase) { skip++; continue; }
      const cur = (r.currency || '').trim().toUpperCase() || baseCurrency;
      const isForeign = cur !== baseCurrency;
      const rate = isForeign ? (parseFloat(r.fxRate) || 0) : 1;
      const travel = {
        industry: 'tourism', pnr: r.pnr, paxName: r.pax, sector: r.routing, airline: r.airline,
        refNo: r.ticket, serviceDate: toISO(r.travelDate), returnDate: r.returnDate ? toISO(r.returnDate) : '',
        date: toISO(r.date), description: r.invNo ? `Ref ${r.invNo}` : 'Air ticket',
        currency: cur, fxRate: rate || 1,
      };

      // SALE — to the client, at Client Total
      if (st.makesSale) {
        const netF = st.clientTotal;
        salesCount++;
        await saveData(user.uid, 'invoices', {
          ...travel, type: 'sales',
          customerName: r.clientName || r.pax,
          fareForeign: netF, netForeign: netF, taxForeign: 0, discountForeign: parseFloat(r.discount) || 0,
          fareAmount: netF * rate, discountAmount: (parseFloat(r.discount) || 0) * rate,
          taxRate: 0, taxAmount: 0, amount: netF * rate,
          status: 'unpaid', invoiceNo: `INV-${String(salesCount).padStart(4, '0')}`,
        });
        sales++;
      }
      // PURCHASE — from the airline/supplier, at Net Supplier Total
      if (st.makesPurchase) {
        const netF = st.netSupplier;
        purCount++;
        await saveData(user.uid, 'invoices', {
          ...travel, type: 'purchase',
          supplierName: r.airline || r.issuedFrom,
          fareForeign: netF, netForeign: netF, taxForeign: 0, discountForeign: 0,
          fareAmount: netF * rate, discountAmount: 0,
          taxRate: 0, taxAmount: 0, amount: netF * rate,
          status: 'unpaid', invoiceNo: `PUR-${String(purCount).padStart(4, '0')}`,
        });
        purch++;
      }
      setDone(sales + purch);
    }
    setImporting(false);
    setStatus(`Imported ${sales} sale(s) and ${purch} purchase(s) from ${rows.length} rows.${skip ? ' ' + skip + ' row(s) skipped.' : ''} All ledgers & reports updated.`);
    setRows([]);
  };

  const fmt = n => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const totals = rows.reduce((a, r) => { const s = rowStats(r); a.client += s.clientTotal; a.supplier += s.netSupplier; a.margin += s.margin; return a; }, { client: 0, supplier: 0, margin: 0 });

  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Upload Daily Sales Report (DSR)</h2>
      <div style={{ color: '#888', fontSize: 14, marginBottom: 18 }}>Import your DSR spreadsheet. Each row can create a <b>client sale</b> and a <b>supplier purchase</b> — full double-entry, so your P&amp;L margin and payables/receivables update automatically.</div>

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', padding: 22, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ ...btn, display: 'inline-block', cursor: 'pointer' }}>
            Choose DSR File (CSV)
            <input type="file" accept=".csv,text/csv" onChange={onFile} style={{ display: 'none' }} />
          </label>
          <button onClick={template} style={ghost}>⬇ Download DSR Template</button>
          {status && <span style={{ fontSize: 13, color: '#555' }}>{status}</span>}
        </div>
        <div style={{ fontSize: 12, color: '#999', marginTop: 12, lineHeight: 1.7 }}>
          Save your Excel DSR as <b>CSV</b> first (File → Save As → CSV), then upload. The importer reads these core columns:<br />
          <b>Client Total</b> → client sale · <b>Net Supplier Total</b> → supplier purchase · plus PNR, Passenger, Routing, Airline, Ticket, Basic, Taxes, Discount, Travel/Return dates.<br />
          For foreign-currency rows, add <b>Currency</b> and <b>FX Rate</b> columns (rate = 1 unit of that currency in your base currency {baseCurrency}).
        </div>
      </div>

      {rows.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Preview ({rows.length} rows)</div>
            <button onClick={doImport} disabled={importing} style={{ ...btn, opacity: importing ? 0.6 : 1 }}>
              {importing ? `Importing... ${done}` : `Import — create sales & purchases`}
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10, marginBottom: 14 }}>
            <Stat label="Client sales total" value={fmt(totals.client)} color={BRAND} />
            <Stat label="Supplier cost total" value={fmt(totals.supplier)} color="#a32d2d" />
            <Stat label="Gross margin" value={fmt(totals.margin)} color="#185fa5" />
          </div>

          <div style={{ overflowX: 'auto', maxHeight: 380, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead><tr>{['Date', 'PNR', 'Passenger', 'Routing', 'Airline', 'Client', 'Client Total', 'Supplier Total', 'Creates'].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #eee', fontSize: 11, color: '#888', textTransform: 'uppercase', position: 'sticky', top: 0, background: '#fff' }}>{h}</th>)}</tr></thead>
              <tbody>
                {rows.map((r, i) => {
                  const st = rowStats(r);
                  return (
                    <tr key={i}>
                      <td style={td}>{r.date}</td>
                      <td style={td}>{r.pnr}</td>
                      <td style={td}>{r.pax}</td>
                      <td style={td}>{r.routing}</td>
                      <td style={td}>{r.airline}</td>
                      <td style={td}>{r.clientName}</td>
                      <td style={td}>{fmt(st.clientTotal)}</td>
                      <td style={td}>{fmt(st.netSupplier)}</td>
                      <td style={td}>
                        {st.makesSale && <span style={{ color: BRAND, fontWeight: 600 }}>Sale</span>}
                        {st.makesSale && st.makesPurchase && ' + '}
                        {st.makesPurchase && <span style={{ color: '#a32d2d', fontWeight: 600 }}>Purchase</span>}
                        {!st.makesSale && !st.makesPurchase && <span style={{ color: '#bbb' }}>skip</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ background: '#f7f8fa', borderRadius: 10, padding: '10px 14px' }}>
      <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color, marginTop: 3 }}>{value}</div>
    </div>
  );
}

const td = { padding: '7px 10px', borderBottom: '1px solid #f2f2f2' };
const btn = { padding: '10px 20px', background: BRAND, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const ghost = { padding: '10px 18px', background: '#fff', color: BRAND, border: `1px solid ${BRAND}`, borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
